import React, { useEffect, useState } from 'react';

import {
  cardIsEditorial,
  cardIsEvent,
  cardIsOffer,
  pageIsEditorial,
} from '../../../helpers/list-page/type-guards';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { IMeetingPlacePageEditorial } from '@/lib/fragments/meetingPlacePageEditorial';
import { ILibraryOpeningHours } from '@/lib/fragments/libraryOpeningHours';
import { ILibraryOpeningHoursException } from '@/lib/fragments/libraryOpeningHoursException';
import { createListCardArray } from '@/helpers/list-page/list-page-helpers';
import { IMeetingPlacePageLegalDocument } from '@/lib/fragments/meetingPlacePageLegalDocument';
import { RenderBlocks } from '@/components/blocks/render-blocks';
import { PageComponentImage } from '../image/page-component-image';
import { BlockWrapper } from '@/components/block-wrapper/block-wrapper';
import { PageComponentMain } from '../main/page-component-main';
import { CardEditorialItem } from '@/components/cards/card-editorial-item';
import { CardEvent } from '@/components/cards/card-event';
import { CardOffer } from '@/components/cards/card-offer';
import ShowMore from '../list-page/show-more';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
  useUrlSubDirectoryDictionary,
} from '@/providers/ConfigProvider';
import { getUrlSubdirectory } from '@/helpers/rendered-page-creation/get-url-subdirectories';
import { WithCountdown } from '@/components/cards/wrapper/WithCountdown';
import LinkWrapper from '@/components/link-wrapper/link-wrapper';
import { useIsSmallScreen } from '@/hooks/use-is-small-screen';
import { isDateTimeInFuture, isDateTimeInPast } from '@/helpers/dates';
import { getListingPageSlugs } from '@/lib/queries/getEventAndOfferListPageSlug';
import { getBrowserClient } from '@/lib/apolloClient';

interface EditorialPageProps {
  page: IMeetingPlacePageEditorial | IMeetingPlacePageLegalDocument;
  integrationId?: string;
  node_locale: string;
  showSocialShare: boolean;
  meetingPlaceOpeningHours?: {
    openingHours?: ILibraryOpeningHours;
    openingHoursExceptions?: ILibraryOpeningHoursException[];
    timezone?: string;
  };
  tenantOpeningHoursDetails?: {
    name: string;
    openingHours: ILibraryOpeningHours;
    openingHoursExceptions: ILibraryOpeningHoursException[];
    timezone: string;
    slug: string;
  }[];
  timezone?: string;
  preview?: boolean;
}

export const EditorialPage = ({
  page,
  integrationId,
  node_locale,
  showSocialShare,
  meetingPlaceOpeningHours,
  tenantOpeningHoursDetails,
  timezone,
  preview = false,
}: EditorialPageProps) => {
  const client = getBrowserClient(preview);
  const isMobile = useIsMobile();
  const isSmallScreen = useIsSmallScreen();
  // const image =
  //   page?.__typename === 'MeetingPlacePageLegalDocument' && 'image' in page
  //     ? page.url
  //     : undefined;

  const isLivliFamilySuccessPage = page?.slug === 'livli-family-success';

  const urlSubDirectorydictionary = useUrlSubDirectoryDictionary();

  const imageURL = page?.mediaField?.jsonBynderAsset?.originalUrl;
  const dictionary = useGetLibraryConstantsDictionaryData();

  const config = useTenantConfig();
  const prefix = config?.GATSBY_SITE_PATH_PREFIX || '';

  const [eventListPageSlug, setEventListPageSlug] = useState<string | null>(
    null,
  );
  const [offerListPageSlug, setOfferListPageSlug] = useState<string | null>(
    null,
  );

  const [timeLeft, setTimeLeft] = useState({
    days: '0',
    hours: '0',
    minutes: '0',
    seconds: '0',
  });

  useEffect(() => {
    const targetDate = new Date(Date.UTC(2026, 5, 2, 8, 0, 0)).getTime();
    const formatTime = (time: number) =>
      time < 10 ? `0${time}` : time.toString();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetDate - now;

      if (distance <= 0) {
        setTimeLeft({ days: '0', hours: '0', minutes: '0', seconds: '0' });
        return;
      }

      setTimeLeft({
        days: formatTime(Math.floor(distance / (1000 * 60 * 60 * 24))),
        hours: formatTime(
          Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        ),
        minutes: formatTime(
          Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        ),
        seconds: formatTime(Math.floor((distance % (1000 * 60)) / 1000)),
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchSlugs = async () => {
      const tag =
        config?.NEXT_PUBLIC_MEETINGPLACE_TAG_CONTENTFUL_ID ||
        config?.MEETINGPLACE_TAG_CONTENTFUL_ID ||
        '';
      const { eventListPageSlug, offerListPageSlug } =
        await getListingPageSlugs(client, tag, node_locale, preview);
      if (eventListPageSlug) setEventListPageSlug(eventListPageSlug);
      if (offerListPageSlug) setOfferListPageSlug(offerListPageSlug);
    };

    fetchSlugs();
  }, [node_locale]);

  const itemsWithPrefix =
    pageIsEditorial(page) && page?.editorialPageCardsCollection
      ? page.editorialPageCardsCollection.items
          ?.filter(Boolean)
          ?.filter((it: any) => it?.__typename && it?.sys?.id)
          ?.map((item: any) => {
            const itemPrefix = getUrlSubdirectory(
              urlSubDirectorydictionary,
              node_locale,
              item?.__typename,
            );
            return {
              ...item,
              slug:
                item.slug && itemPrefix
                  ? `${itemPrefix}/${item.slug}`
                  : item.slug,
            };
          })
      : [];

  const allCards =
    (itemsWithPrefix &&
      createListCardArray(
        itemsWithPrefix,
        undefined,
        undefined,
        undefined,
        node_locale,
        prefix,
      )) ||
    [];
  const _cardsListSize = 4;

  const offerCards = allCards
    ?.filter((card) => cardIsOffer(card))
    ?.filter((card) =>
      isDateTimeInFuture(card?.validTo?.toString(), timezone ?? ''),
    )
    ?.slice(0, _cardsListSize);
  const eventCards = allCards
    ?.filter((card) => cardIsEvent(card))
    ?.filter(
      (card) =>
        !card?.endDate ||
        !isDateTimeInPast(card.endDate.toString(), timezone ?? ''),
    )
    ?.slice(0, _cardsListSize);
  const editorialCards = allCards
    ?.filter((card) => cardIsEditorial(card))
    ?.slice(0, _cardsListSize);

  return (
    <div className="grid grid-cols-11 gap-6 pt-5 lg:gap-5">
      <div className="col-span-11 lg:col-span-5">
        <PageComponentImage
          imageURL={imageURL}
          imageAlt={page?.mediaField?.altText}
          stickyImage={true}
          node_locale={node_locale}
        />
      </div>
      <div className="col-span-11 lg:col-span-6">
        <PageComponentMain
          header={page?.name}
          blocks={page?.mainBlockCollection?.items}
          richText={page?.body}
          node_locale={node_locale}
          showSocialShare={showSocialShare}
          meetingPlaceOpeningHours={meetingPlaceOpeningHours}
          tagItems={
            pageIsEditorial(page)
              ? page?.tagsCollection?.items?.filter(Boolean)?.map((tag) => ({
                  id: tag.sys.id,
                  tagName: tag.tagName,
                  colour: tag.colour,
                }))
              : undefined
          }
          panelclasses={isMobile ? '!bg-background-primary' : ''}
        />
        {isLivliFamilySuccessPage && (
          <div className="-mx-6 bg-brand-white pt-16 lg:-mx-0 lg:mb-5 lg:pt-0">
            <BlockWrapper className="rounded-card-mobile px-5 py-10 lg:px-24 lg:py-12 bg-dynamicStroke4">
              <h1 className="text-center text-headingl font-title">
                {dictionary?.livliFamilyLiveCounterTitle || ''}
              </h1>
              <h1 className="text-center text-headingl font-title">
                {dictionary?.livliFamilyLiveCounterText}
              </h1>
              <div className="flex gap-x-4 justify-center text-center mt-6">
                <div className="flex flex-col items-center w-16">
                  <p className="text-3xl font-title text-headingxxl text-neutral-grey-700 md:text-4xl">
                    {timeLeft.days}
                  </p>
                  <p className="text-bodyl text-neutral-grey-700 tracking-wider">
                    Días
                  </p>
                </div>
                <div className="flex flex-col items-center w-16">
                  <p className="text-3xl font-title text-headingxxl text-neutral-grey-700 md:text-4xl">
                    {timeLeft.hours}
                  </p>
                  <p className="text-bodyl  text-neutral-grey-700 tracking-wider">
                    Horas
                  </p>
                </div>
                <div className="flex flex-col items-center w-16">
                  <p className="text-3xl font-title text-headingxxl text-neutral-grey-700 md:text-4xl">
                    {timeLeft.minutes}
                  </p>
                  <p className="text-bodyl text-neutral-grey-700 tracking-wider">
                    Minutos
                  </p>
                </div>
                <div className="flex flex-col items-center w-16">
                  <p className="text-3xl font-title text-headingxxl text-neutral-grey-700  md:text-4xl">
                    {timeLeft.seconds}
                  </p>
                  <p className="text-bodyl text-neutral-grey-700 tracking-wider">
                    Segundos
                  </p>
                </div>
              </div>
            </BlockWrapper>
          </div>
        )}
        {page?.blocksCollection && (
          <div
            className={`-mx-6 bg-brand-white ${isLivliFamilySuccessPage ? 'pt-5' : 'pt-16'} lg:-mx-0 lg:mb-5 lg:pt-0`}
          >
            {page?.blocksCollection?.items.map((block, index) => (
              <BlockWrapper
                key={index}
                className="mb-6 rounded-card-mobile px-6 py-11 last:mb-0 lg:p-20 xl:p-24"
              >
                <RenderBlocks
                  blocks={[block]}
                  integrationId={integrationId}
                  node_locale={node_locale}
                  tenantOpeningHoursDetails={tenantOpeningHoursDetails}
                  meetingPlaceOpeningHours={meetingPlaceOpeningHours}
                />
              </BlockWrapper>
            ))}
          </div>
        )}

        <div className="-mx-6 bg-brand-white sm:px-6 pt-6 lg:m-0 lg:p-0">
          {offerCards && offerCards?.length > 0 && (
            <>
              <h2 className="px-5 sm:px-0 mb-3 font-title text-headingm text-neutral-grey-900">
                {dictionary?.offers}
              </h2>
              <div className="mb:0 py-2 px-5 sm:p-2 flex gap-4 overflow-x-auto scroll-smooth scrollbar-mb [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden mb-6 sm:grid sm:grid-cols-2 sm:gap-5">
                {offerCards?.map((offer, index) => {
                  return (
                    <WithCountdown key={offer?.id} card={offer}>
                      {({ countdownState }) => (
                        <CardOffer
                          card={offer}
                          key={offer.id}
                          cardIndex={index}
                          countdownState={countdownState}
                          fixedWidth={isSmallScreen}
                        />
                      )}
                    </WithCountdown>
                  );
                })}
              </div>

              <div>
                {offerListPageSlug && (
                  <LinkWrapper
                    data={`/${node_locale}/${offerListPageSlug}`}
                    className="flex ml-auto mr-5 sm:mr-0 w-fit text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
                  >
                    {dictionary?.seeAllOffers}
                  </LinkWrapper>
                )}
              </div>
            </>
          )}
        </div>

        <div className="-mx-6 bg-brand-white sm:px-6 pt-6 lg:m-0 lg:p-0">
          {eventCards && eventCards?.length > 0 && (
            <>
              <h2 className="px-5 sm:px-0 lg:mt-6 mb-3 font-title text-headingm text-neutral-grey-900">
                {dictionary?.events}
              </h2>
              <div className="mb:0 py-2 px-5 sm:p-2 flex gap-4 overflow-x-auto scroll-smooth scrollbar-mb [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden mb-6 sm:grid sm:grid-cols-2 sm:gap-5">
                {eventCards?.map((event, index) => {
                  return (
                    <CardEvent
                      card={event}
                      key={event.id}
                      cardIndex={index}
                      fixedWidth={isSmallScreen}
                    />
                  );
                })}
              </div>

              <div>
                {eventListPageSlug && (
                  <LinkWrapper
                    data={`/${node_locale}/${eventListPageSlug}`}
                    className="flex ml-auto mr-5 sm:mr-0 w-fit text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
                  >
                    {dictionary?.seeAllEvents}
                  </LinkWrapper>
                )}
              </div>
            </>
          )}
        </div>

        <div className="-mx-6 bg-brand-white sm:px-6 pt-6 lg:m-0 lg:p-0">
          {editorialCards && editorialCards?.length > 0 && (
            <>
              <h2 className="px-5 sm:px-0 lg:mt-6 mb-3 font-title text-headingm text-neutral-grey-900">
                {dictionary?.moreInfo}
              </h2>
              <div className="mb:0 py-2 px-5 sm:p-2 flex gap-4 overflow-x-auto scroll-smooth scrollbar-mb [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pb-6 sm:grid sm:grid-cols-2 sm:gap-5">
                {editorialCards?.map((card, index) => {
                  return (
                    <div className="w-[280px] sm:w-auto" key={card.id}>
                      <CardEditorialItem
                        card={card}
                        key={card.id}
                        cardIndex={index}
                        fixedWidth={isSmallScreen}
                      />
                    </div>
                  );
                })}
              </div>

              {/* <div>
                <LinkWrapper
                  data={`/${node_locale}`}
                  className="flex ml-auto w-fit text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
                >
                  See all
                </LinkWrapper>
              </div> */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

